import { Provider, WorkflowComponent, WorkflowComponentParamType } from '@cartodb/workflows-engine'
import { CustomComponentsDict, Workflow, ExtensionPackageParsedModel, WorkflowNodePayload } from '../types'
import { WORKFLOW_NODE_TYPE } from '../constants'
import { WorkflowNodeStatus } from '../enums'
import { ProviderType } from 'features/connections/types'
import { Edge, Node, NodeProps } from 'reactflow'
import { produce } from 'immer'
import { getSchemaFromParent } from './sources'
import { schemaToOptions } from './schemaToOptions'

export const componentMatchSearch = (search: string) => (c: WorkflowComponent) =>
  c.metadata.title.toLowerCase().includes(search.toLowerCase()) ||
  c.metadata.name.toLowerCase().includes(search.toLowerCase())

export const isStarred = (c: WorkflowComponent) => !!c.metadata.starred

export function isNodeVersionOutdated({
  nodeVersion = '1',
  component,
  provider
}: {
  nodeVersion?: string
  component: WorkflowComponent
  provider?: ProviderType | Provider
}) {
  const currentComponentVersion = component.getVersion(provider as unknown as Provider)
  const nodeMajorVersion = nodeVersion.split('.')[0]
  const currentMajorVersion = currentComponentVersion.split('.')[0]
  return nodeMajorVersion < currentMajorVersion
}

export function getNodeVersionState({
  node,
  component,
  provider
}: {
  node: Node<WorkflowNodePayload> | NodeProps<WorkflowNodePayload>
  component?: WorkflowComponent | undefined
  provider?: ProviderType | Provider
}): WorkflowNodeStatus | undefined {
  if (!isVersionedNode(node)) {
    return undefined
  }

  if (!component || !node.data.label) {
    return WorkflowNodeStatus.Removed
  }

  if (component?.metadata.deprecated) {
    return WorkflowNodeStatus.Deprecated
  }

  if (component && isNodeVersionOutdated({ nodeVersion: node.data.version, component, provider })) {
    return WorkflowNodeStatus.Outdated
  }
  return undefined
}

export function isVersionedNode(node: Pick<Node, 'type'>) {
  return node.type && [WORKFLOW_NODE_TYPE.Generic].includes(node.type)
}

export function sanitizeLinkResource(data: string) {
  return data
    .replace(/\s*\/\s*|\s+/g, '-')
    .replace(/[()]/g, '')
    .toLocaleLowerCase()
}

export const componentsToMap = <T>(components: T[]) => {
  return components.reduce((result, component) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    result[component.metadata.name] = component
    return result
  }, {} as CustomComponentsDict<T>)
}

export const enrichComponentsWithExtension = <T>(
  components: CustomComponentsDict<T>,
  extensions: ExtensionPackageParsedModel[]
) => {
  extensions.forEach((extension) => {
    extension.metadata.components.forEach((item) => {
      if (components[item.name]) {
        components[item.name].meta = {
          isExtension: true,
          logo: item.icon,
          groupLogo: extension.metadata.icon,
          externalReference: item.externalReference
        }
      }
    })
  })

  return components
}

// TODO add tests (if it won't be removed with upcoming improvements)
export const setDefaultsForNode = (workflow: Workflow, node: Node) => {
  let hasChanges = false
  const updatedNode = produce(node, (draft) => {
    draft.data.inputs = node.data.inputs.map((input) => {
      if (
        input.optional ||
        input.noDefault ||
        ![WorkflowComponentParamType.Column, WorkflowComponentParamType.Selection].includes(input.type)
      ) {
        return input
      }

      const schema = getSchemaFromParent({
        edges: workflow?.config.edges as Edge[],
        nodeId: node?.id as string,
        parent: input.parent as string
      })

      if (schema) {
        const options = schemaToOptions(workflow.config.connectionProvider as Provider, input, schema)

        if (options?.length && !input.value) {
          hasChanges = true

          return {
            ...input,
            value: options[0]
          }
        }
      }

      return input
    })
  })

  return { node: updatedNode, hasChanges }
}
